import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import React from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { AdvancedParameters } from '@/core/AdvancedParameters.atom';
import { FakeLink } from '@/core/FakeLink';
import { faAngleDown, faArrows, faChevronRight, faClose, faPlus } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Badge, Button, Icon } from '@seeqdev/qomponents';
import { colors } from '@seeqdev/qomponents/colors.stylex';
import type { IconDefinition } from '@seeqdev/icons';

interface FormConditionProps {
  conditionName: string;
  children: React.ReactNode;
  show?: boolean;
}

export const FormCondition = ({ conditionName, children, show }: FormConditionProps) => (
  <Field name={conditionName} subscription={{ value: true }} key={`${conditionName}_field`}>
    {() => (show ? children : null)}
  </Field>
);

interface ConditionalFormBlockProps {
  isConditional?: boolean;
  show?: boolean;
  name: string;
  children: React.ReactNode;
}

export const ConditionalFormBlock = ({ isConditional, show, name, children }: ConditionalFormBlockProps) => {
  if (isConditional) {
    return (
      <FormCondition conditionName={`${name}_condition`} show={show} key={`${name}_condition`}>
        {children}
      </FormCondition>
    );
  }
  return <React.Fragment key={`${name}_conditionBlock`}>{children}</React.Fragment>;
};

interface FormBlockProps {
  formBlockXstyle?: stylex.StyleXStyles;
  name: string;
  children: React.ReactNode;
  formBlockClassNames?: string;
}

export const FormBlock = ({ name, children, formBlockClassNames, formBlockXstyle }: FormBlockProps) => {
  return (
    <div
      key={name}
      {...stylex.props(styles.row, formBlockXstyle)}
      className={clsx('formElement', stylex.props(styles.row, formBlockXstyle).className, formBlockClassNames)}
      id={`${name}-wrapper`}>
      {children}
    </div>
  );
};

interface FormRowBlockProps {
  xstyle?: stylex.StyleXStyles;
  name: string;
  testId?: string;
  extraClassNames?: string;
  children: React.ReactNode;
}

export const FormRowBlock = ({ name, testId, extraClassNames, children, xstyle }: FormRowBlockProps) => (
  <div
    key={`${name}_row`}
    {...stylex.props(styles.row, xstyle)}
    className={clsx(stylex.props(styles.row, xstyle).className, extraClassNames)}
    id={name}
    data-testid={testId}>
    {children}
  </div>
);

interface TitledFormRowBlockProps {
  xstyle?: stylex.StyleXStyles;
  wrapperXstyle?: stylex.StyleXStyles;
  name: string;
  testId?: string;
  extraClassNames?: string;
  wrapperClassNames?: string;
  title: string;
  helpText?: string;
  children: React.ReactNode;
}

export const TitledFormRowBlock = ({
  name,
  testId,
  extraClassNames,
  title,
  helpText,
  children,
  wrapperClassNames,
  xstyle,
  wrapperXstyle,
}: TitledFormRowBlockProps) => {
  const { t } = useTranslation();

  return (
    <div
      key={`${name}_row`}
      {...stylex.props(wrapperXstyle)}
      className={clsx(stylex.props(wrapperXstyle).className, wrapperClassNames)}
      id={name}
      data-testid={testId}>
      <div {...stylex.props(styles.groupHeading, styles.rowHeading)}>{t(title)}</div>
      {helpText && <div {...stylex.props(styles.rowHelp)}>{t(helpText)}</div>}
      <div
        {...stylex.props(styles.row, xstyle)}
        className={clsx(stylex.props(styles.row, xstyle).className, extraClassNames)}>
        {children}
      </div>
    </div>
  );
};

interface RemovableFormRowBlockProps {
  xstyle?: stylex.StyleXStyles;
  wrapperXstyle?: stylex.StyleXStyles;
  name: string;
  testId?: string;
  extraClassNames?: string;
  wrapperClassNames?: string;
  children: React.ReactNode;
  hideRemoveIcon?: boolean;
  removeAction: () => void;
}

export const RemovableFormRowBlock = ({
  name,
  testId,
  extraClassNames,
  wrapperClassNames,
  children,
  hideRemoveIcon,
  removeAction,
  xstyle,
  wrapperXstyle,
}: RemovableFormRowBlockProps) => (
  <div
    {...stylex.props(wrapperXstyle)}
    className={clsx('removableRowWrapper', stylex.props(wrapperXstyle).className, wrapperClassNames)}
    data-testid={`${testId}_wrapper`}>
    {!hideRemoveIcon && <Icon icon={faClose} testId="removeIcon" extraClassNames="removeIcon" onClick={removeAction} />}
    <div
      key={`${name}_row`}
      {...stylex.props(styles.row, xstyle)}
      className={clsx(stylex.props(styles.row, xstyle).className, extraClassNames)}
      id={name}
      data-testid={testId}>
      {children}
    </div>
  </div>
);

interface AdvancedFormBlockProps {
  xstyle?: stylex.StyleXStyles;
  name: string;
  testId?: string;
  toolName: string;
  toolId: string;
  alternateName?: string;
  extraClassNames?: string;
  iconStyle?: string;
  collapsed: boolean;
  onToggle?: (collapsed: boolean) => void;
  children: React.ReactNode;
}

export const AdvancedFormBlock = ({
  name,
  testId,
  toolName,
  toolId,
  alternateName,
  extraClassNames,
  iconStyle,
  collapsed,
  onToggle,
  children,
  xstyle,
}: AdvancedFormBlockProps) => (
  <div
    key={`${name}_wrapper`}
    {...stylex.props(styles.advanced, xstyle)}
    className={clsx('advancedPanel toolOptions', stylex.props(styles.advanced, xstyle).className, extraClassNames)}
    data-testid={testId}
    id={name}>
    <AdvancedParameters
      toolName={toolName}
      toolId={toolId}
      alternateName={alternateName}
      iconStyle={iconStyle}
      collapsed={collapsed}
      onToggle={onToggle}>
      {children}
    </AdvancedParameters>
  </div>
);

export const FormGroupBlock = ({
  name,
  testId,
  formGroupExtraClassNames,
  formGroupXstyle,
  showBracket,
  children,
  showScroll,
  showGroupStyles,
}: {
  name: string;
  testId?: string;
  formGroupExtraClassNames?: string;
  formGroupXstyle?: stylex.StyleXStyles;
  showBracket?: boolean;
  children: React.ReactNode;
  showScroll?: boolean;
  showGroupStyles?: boolean;
}) => (
  <div
    key={`${name}_wrapper`}
    {...stylex.props(
      styles.group,
      showScroll ? styles.scrollGroup : styles.fillGroup,
      showGroupStyles && styles.groupContent,
      formGroupXstyle,
    )}
    className={clsx(
      stylex.props(
        styles.group,
        showScroll ? styles.scrollGroup : styles.fillGroup,
        showGroupStyles && styles.groupContent,
        formGroupXstyle,
      ).className,
      formGroupExtraClassNames,
      {
        leftBracket: showBracket,
      },
    )}
    data-testid={testId || name}
    id={name}>
    {children}
  </div>
);

interface FormGroupWithTitleBlockProps {
  xstyle?: stylex.StyleXStyles;
  name: string;
  testId?: string;
  extraClassNames?: string;
  showBracket?: boolean;
  children: React.ReactNode;
  showScroll?: boolean;
  showTitle?: boolean;
  title?: string;
  titleLink?: string;
  titleLinkAction?: () => void;
  additionalTitleLink?: string;
  additionalTitleLinkAction?: () => void;
}

export const FormGroupWithTitleBlock = ({
  name,
  testId,
  extraClassNames,
  showBracket,
  children,
  showScroll,
  showTitle,
  title,
  titleLink,
  titleLinkAction,
  additionalTitleLink,
  additionalTitleLinkAction,
  xstyle,
}: FormGroupWithTitleBlockProps) => (
  <div
    key={`${name}_wrapper`}
    {...stylex.props(styles.group, showScroll ? styles.scrollGroup : styles.fillGroup, xstyle)}
    className={clsx(
      stylex.props(styles.group, showScroll ? styles.scrollGroup : styles.fillGroup, xstyle).className,
      extraClassNames,
      {
        leftBracket: showBracket,
      },
    )}
    data-testid={testId || name}
    id={name}>
    {showTitle && (
      <div {...stylex.props(styles.groupHeading)}>
        <span {...stylex.props(styles.headingText)}>{title}</span>
        <div {...stylex.props(styles.headingActions)}>
          {additionalTitleLink && (
            <FakeLink onClick={additionalTitleLinkAction} extraClassNames={stylex.props(styles.headingLink).className}>
              {additionalTitleLink}
            </FakeLink>
          )}
          {titleLink && (
            <FakeLink onClick={titleLinkAction} extraClassNames={stylex.props(styles.headingLink).className}>
              {titleLink}
            </FakeLink>
          )}
        </div>
      </div>
    )}
    {children}
  </div>
);

interface RemovableFormBlockProps {
  xstyle?: stylex.StyleXStyles;
  iconXstyle?: stylex.StyleXStyles;

  testId?: string;
  children: React.ReactNode;
  iconAction?: () => void;
  hideIcon?: boolean;
  extraClassNames?: string;
  iconHasMargin?: boolean;
  iconTooltip?: string;
  showLabelAfterRows?: boolean;
  labelAfterRows?: string;
  addBlockButtonAction?: () => void;
  addBlockButtonTooltip?: string;
  showAddBlockButton?: boolean;
}

export const RemovableFormBlock = ({
  testId,
  children,
  iconAction,
  hideIcon,
  extraClassNames,
  xstyle,
  iconXstyle,
  iconHasMargin,
  iconTooltip,
  showLabelAfterRows,
  labelAfterRows,
  addBlockButtonAction,
  addBlockButtonTooltip,
  showAddBlockButton,
}: RemovableFormBlockProps) => {
  const icon = !hideIcon && (
    <div
      onClick={iconAction}
      {...stylex.props(iconXstyle && styles.removableIcon, iconXstyle)}
      className={clsx(
        iconXstyle
          ? stylex.props(styles.removableIcon, iconXstyle).className
          : ['cursorPointer', iconHasMargin ? 'closeIconWrapperMargin' : 'closeIconWrapper'],
      )}>
      <Icon icon={faClose} testId="deleteIcon" tooltip={iconTooltip} />
    </div>
  );

  const wrapperClass = clsx(
    xstyle ? stylex.props(styles.removable, xstyle).className : 'removableBlockWrapper',
    extraClassNames,
  );
  const labelClasses = 'addFormBlockLabel rightAlignedLabel';

  return iconHasMargin ? (
    <div className="inputContainer">
      {icon}
      <div {...stylex.props(xstyle && styles.removable, xstyle)} className={wrapperClass}>
        {children}
      </div>
      {showLabelAfterRows && <div className={labelClasses}>{labelAfterRows}</div>}
      {showAddBlockButton && (
        <div className={labelClasses}>
          <Button
            testId="addBlockButton"
            variant="theme"
            onClick={addBlockButtonAction}
            icon={faPlus}
            iconStyle="white"
            size="sm"
            tooltip={addBlockButtonTooltip}
            xstyle={styles.addButton}
          />
        </div>
      )}
    </div>
  ) : (
    <div {...stylex.props(xstyle && styles.removable, xstyle)} className={wrapperClass} data-testid={testId}>
      {icon}
      <>{children}</>
    </div>
  );
};

export interface CollapsibleFormGroupProps {
  xstyle?: stylex.StyleXStyles;
  iconXstyle?: stylex.StyleXStyles;
  contentXstyle?: stylex.StyleXStyles;
  labelXstyle?: stylex.StyleXStyles;

  testId?: string;
  children: React.ReactNode;
  deleteIconAction: () => void;
  hideDeleteIcon?: boolean;
  additionalIcon?: IconDefinition;
  additionalIconAction?: () => void;
  additionalIconAriaLabel?: string;
  additionalIconAsButton?: boolean;
  additionalIconTestId?: string;
  additionalIconTooltip?: string;
  extraClassNames?: string;
  labelExtraClassNames?: string;
  isCollapsed: boolean;
  toggleCollapse: () => void;
  label: string;
  labelSuffix?: React.ReactNode;
  isLast?: boolean;
  isNewlyAdded?: boolean;
  newLabel?: string;
  dragRef?: React.Ref<HTMLDivElement>;
  isDragging?: boolean;
  dragHandleTooltip?: string;
}

export const CollapsibleFormBlock = ({
  testId,
  children,
  deleteIconAction,
  hideDeleteIcon,
  additionalIcon,
  additionalIconAction,
  additionalIconAriaLabel,
  additionalIconAsButton = false,
  additionalIconTestId,
  additionalIconTooltip,
  extraClassNames,
  xstyle,
  iconXstyle,
  contentXstyle,
  labelXstyle,
  labelExtraClassNames,
  isCollapsed,
  toggleCollapse,
  label,
  labelSuffix,
  isLast,
  isNewlyAdded,
  newLabel,
  dragRef,
  isDragging,
  dragHandleTooltip,
}: CollapsibleFormGroupProps) => {
  const { t } = useTranslation();

  const icon = (
    <div
      {...stylex.props((xstyle || iconXstyle) && styles.collapsibleIcon, iconXstyle)}
      className={
        xstyle || iconXstyle
          ? stylex.props(styles.collapsibleIcon, iconXstyle).className
          : 'cursorPointer closeIconWrapper'
      }>
      <Icon onClick={toggleCollapse} icon={isCollapsed ? faChevronRight : faAngleDown} testId="toggleIcon" />
      {dragRef ? (
        <Icon
          icon={faArrows}
          xstyle={styles.actionIcon}
          extraClassNames="dragHandle"
          testId="dragHandle"
          tooltip={dragHandleTooltip}
        />
      ) : null}
      {additionalIcon && additionalIconAction ? (
        additionalIconAsButton ? (
          <button
            type="button"
            {...stylex.props(styles.additionalAction)}
            data-testid={additionalIconTestId ?? 'additionalIcon'}
            aria-label={additionalIconAriaLabel ?? (additionalIconTooltip ? t(additionalIconTooltip) : undefined)}
            onClick={(event) => {
              event.stopPropagation();
              additionalIconAction();
            }}
            onKeyDown={(event) => {
              // Keep Enter/Space on the header copy control from toggling collapse.
              if (event.key === 'Enter' || event.key === ' ') {
                event.stopPropagation();
              }
            }}>
            <Icon icon={additionalIcon} tooltip={additionalIconTooltip ? t(additionalIconTooltip) : undefined} />
          </button>
        ) : (
          <Icon
            onClick={additionalIconAction}
            icon={additionalIcon}
            xstyle={styles.additionalIcon}
            testId="additionalIcon"
            tooltip={additionalIconTooltip ? t(additionalIconTooltip) : undefined}
          />
        )
      ) : null}
      {hideDeleteIcon ? null : (
        <Icon onClick={deleteIconAction} icon={faClose} xstyle={styles.actionIcon} testId="deleteIcon" />
      )}
    </div>
  );
  const newBadge = <Badge xstyle={styles.newBadge}>{newLabel}</Badge>;
  const containerProps = stylex.props(
    xstyle && styles.collapsible,
    xstyle && isLast && styles.last,
    xstyle,
    dragRef && styles.dragging(isDragging ? 0.1 : 1),
  );

  return (
    <div
      ref={dragRef}
      {...containerProps}
      className={clsx(
        !xstyle && ['collapsibleBlockWrapper grayBorder', { bottomBorderRadius: isLast }],
        containerProps.className,
        extraClassNames,
      )}
      data-testid={testId}>
      {icon}
      <div
        {...stylex.props(styles.label, !isCollapsed && styles.expandedLabel, labelXstyle)}
        className={clsx(
          stylex.props(styles.label, !isCollapsed && styles.expandedLabel, labelXstyle).className,
          labelExtraClassNames,
        )}
        onClick={toggleCollapse}>
        <span {...stylex.props(styles.labelToggle)}>{label}</span>
        {labelSuffix ? (
          <span {...stylex.props(styles.labelSuffix)} onClick={(event) => event.stopPropagation()}>
            {labelSuffix}
          </span>
        ) : null}
        {isNewlyAdded ? newBadge : null}
      </div>
      <div
        {...stylex.props(contentXstyle)}
        className={
          contentXstyle
            ? stylex.props(contentXstyle).className
            : isCollapsed
              ? 'collapsibleContent'
              : 'expandedCollapsibleContent'
        }
        hidden={contentXstyle ? isCollapsed : undefined}>
        {children}
      </div>
    </div>
  );
};

interface DisplayOnlyFormElementWrapperProps {
  xstyle?: stylex.StyleXStyles;
  extraClassNames?: string;
  testId?: string;
  children: React.ReactNode;
}

export const DisplayOnlyFormElementWrapper = ({
  extraClassNames,
  testId,
  children,
  xstyle,
}: DisplayOnlyFormElementWrapperProps) => (
  <div {...stylex.props(xstyle)} className={clsx(stylex.props(xstyle).className, extraClassNames)} data-testid={testId}>
    {children}
  </div>
);

const styles = stylex.create({
  addButton: { width: 35 },
  actionIcon: { marginLeft: 10 },
  additionalIcon: { marginLeft: '.625rem' },
  newBadge: { marginLeft: 7 },
  labelSuffix: { marginLeft: '.625rem', display: 'inline-block' },
  labelToggle: { cursor: 'pointer', textDecoration: 'underline' },
  additionalAction: {
    marginLeft: '.625rem',
    cursor: 'pointer',
    borderRadius: '.25rem',
    borderWidth: 0,
    backgroundColor: 'transparent',
    padding: 0,
    outlineWidth: { ':focus-visible': 2 },
    outlineOffset: { ':focus-visible': 2 },
  },
  groupHeading: {
    display: 'flex',
    alignItems: 'center',
    borderWidth: '.0625rem',
    borderStyle: 'solid',
    borderColor: colors.formGroupBorder,
    borderRadius: '.3125rem .3125rem 0 0',
    fontWeight: 'bold',
    width: '100%',
    paddingBlock: '.5rem',
    paddingInline: '.75rem',
    boxSizing: 'border-box',
    backgroundColor: colors.formGroupHeadingBackground,
  },
  rowHeading: { marginBottom: 10 },
  rowHelp: {
    paddingInline: '.75rem',
    paddingBottom: '.5rem',
    textAlign: 'center',
    fontSize: '.875rem',
    lineHeight: '1.25rem',
    fontStyle: 'italic',
  },
  headingText: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  headingActions: { marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '1rem' },
  headingLink: {
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textDecoration: { 'default': 'none', ':hover': 'underline', ':active': 'underline', ':focus': 'underline' },
  },
  dragging: (opacity: number) => ({ opacity }),
  row: { display: 'flex', flexDirection: 'row' },
  group: { display: 'flex', flexDirection: 'column' },
  scrollGroup: { flex: '1 1 auto', overflow: 'hidden' },
  fillGroup: { flex: '1 1 0%' },
  groupContent: { padding: 10, marginTop: 0, marginBottom: 8 },
  advanced: { display: 'flex', flexDirection: 'column', marginBottom: '.9375rem' },
  removable: {
    padding: '.625rem',
    marginBottom: '.625rem',
    borderWidth: '.0625rem',
    borderStyle: 'solid',
    borderColor: colors.formGroupBorder,
    position: 'relative',
    borderRadius: '.3125rem',
  },
  removableIcon: { position: 'absolute', right: '.625rem', top: '.4375rem', cursor: 'pointer' },
  collapsible: {
    paddingInline: '.625rem',
    paddingBlock: '.3125rem',
    borderWidth: '.0625rem',
    borderStyle: 'solid',
    borderColor: colors.sidebarBorder,
    position: 'relative',
  },
  collapsibleIcon: { position: 'absolute', right: '.625rem', cursor: 'pointer' },
  label: { fontWeight: 700 },
  expandedLabel: { marginBottom: 10 },
  last: { borderRadius: '0 0 .3125rem .3125rem' },
});

export { styles as formBuildingBlockStyles };
